import type { DoctorSlugResponse } from '@/common/apis/services/doctor/validateDoctorSlug';
import { splunkInstance } from '@/common/services/splunk';
import { isDoctorProfileSelfRedirect } from './isDoctorProfileSelfRedirect';
import type { DoctorSlugNavigation } from './resolveDoctorProfileSlugNavigation';

/**
 * Splunk events for a resolved slug navigation (moved verbatim from getAggregatedProfileData so the
 * SSR aggregator and the client progressive hook send identical payloads).
 */
export function reportSlugNavigation(
  slug: string,
  navigation: DoctorSlugNavigation,
  isServer: boolean,
  slugValidation: DoctorSlugResponse,
) {
  const result = slugValidation;
  if (navigation.type === 'redirect') {
    splunkInstance('doctor-profile').sendEvent({
      group: 'doctor_slug_redirect',
      type: navigation.reason === 'full_profile' ? 'full_profile_alias' : 'slug_validation_alias',
      event: {
        original_slug: slug,
        redirect_route: navigation.destination,
        redirect_status_code: navigation.statusCode,
        reason: navigation.reason,
        isServer: isServer,
        timestamp: new Date().toISOString(),
      },
    });
    return;
  }

  if (navigation.type === 'notFound') {
    splunkInstance('doctor-profile').sendEvent({
      group: 'doctor_profile_404',
      type: 'page_not_found',
      event: {
        original_slug: slug,
        error_message: 'error' in result ? result.error : 'Slug not found',
        isServer: isServer,
        timestamp: new Date().toISOString(),
      },
    });
    return;
  }

  if ('redirect' in result && isDoctorProfileSelfRedirect(slug, result.redirect.route)) {
    splunkInstance('doctor-profile').sendEvent({
      group: 'doctor_slug_self_redirect_ignored',
      type: 'self_redirect_ignored',
      event: {
        original_slug: slug,
        redirect_route: result.redirect.route,
        isServer: isServer,
        timestamp: new Date().toISOString(),
      },
    });
  } else if (navigation.slugInfo) {
    splunkInstance('doctor-profile').sendEvent({
      group: 'profile_slug_validation_primary',
      type: 'slug_validation_primary_success',
      event: {
        original_slug: slug,
        validated_slug: navigation.validatedSlug,
        doctor_id: navigation.slugInfo.id,
        user_id: navigation.slugInfo.user_id,
        isServer: isServer,
        timestamp: new Date().toISOString(),
      },
    });
  }
}

/** Splunk event for a rejected slug validation (page still renders with the raw slug). */
export function reportSlugValidationError(slug: string, reason: unknown, isServer: boolean) {
  splunkInstance('doctor-profile').sendEvent({
    group: 'profile_slug_validation_error',
    type: 'slug_validation_error',
    event: {
      original_slug: slug,
      error_message: reason instanceof Error ? reason.message : String(reason),
      isServer: isServer,
      timestamp: new Date().toISOString(),
    },
  });
}
